import React, { useEffect, useMemo, useState } from "react";
import { Box, CardContent, CircularProgress, Alert, Typography } from "@mui/material";
import GlassCard from "../Cards/GlassCard";
import CommonDataGrid from "./CommonDataGrid";
import axiosInstance from "../../services/axiosInstance";

const LeetCodeDropOffSection = ({ batchId }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [rows, setRows] = useState([]);

  useEffect(() => {
    if (!batchId) return;
    let active = true;
    setLoading(true);
    setError("");

    axiosInstance
      .get("/analytics/leetcode/drop-off", { params: { batchId } })
      .then(({ data }) => {
        if (!active) return;
        const list = Array.isArray(data?.students) ? data.students : [];
        setRows(
          list.map((item, idx) => ({
            id: item.student_id || idx,
            name: item.name || "Unknown",
            rollNumber: item.rollNumber || "—",
            previousActivity: item.previousActivity || 0,
            currentActivity: item.currentActivity || 0,
            dropPct: item.dropPct || 0,
          }))
        );
      })
      .catch((err) => {
        if (!active) return;
        setRows([]);
        setError(err?.response?.data?.message || "Failed to load drop-off insights.");
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [batchId]);

  const columns = useMemo(
    () => [
      { field: "name", headerName: "Name", minWidth: 180, flex: 1.1 },
      { field: "rollNumber", headerName: "Roll Number", minWidth: 130, flex: 0.8 },
      {
        field: "previousActivity",
        headerName: "Previous Activity",
        minWidth: 140,
        align: "center",
        headerAlign: "center",
      },
      {
        field: "currentActivity",
        headerName: "Current Activity",
        minWidth: 130,
        align: "center",
        headerAlign: "center",
      },
      {
        field: "dropPct",
        headerName: "Drop %",
        minWidth: 110,
        align: "center",
        headerAlign: "center",
        renderCell: ({ value }) => (
          <Typography variant="body2" fontWeight={700} color="error.main">
            {value}%
          </Typography>
        ),
      },
    ],
    []
  );

  return (
    <GlassCard hoverable={false} sx={{ borderRadius: 3, mt: 2 }}>
      <CardContent sx={{ p: 2.5 }}>
        <Typography variant="subtitle1" fontWeight={700} mb={0.5}>
          Drop-off Detection
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Declining students where current week activity dropped by more than 50% vs previous week
        </Typography>

        {loading && (
          <Box sx={{ py: 4, display: "flex", justifyContent: "center" }}>
            <CircularProgress size={28} />
          </Box>
        )}

        {!loading && error && (
          <Alert severity="error" sx={{ mt: 1.5, borderRadius: 2 }}>
            {error}
          </Alert>
        )}

        {!loading && !error && rows.length === 0 && (
          <Alert severity="success" sx={{ mt: 1.5, borderRadius: 2 }}>
            No declining students detected this week.
          </Alert>
        )}

        {!loading && !error && rows.length > 0 && (
          <Box sx={{ mt: 1.5, height: 340 }}>
            <CommonDataGrid
              firstColumnField="name"
              rows={rows}
              columns={columns}
              defaultPageSize={10}
              pageSizeOptions={[10, 25, 50]}
              disableRowSelectionOnClick
              initialState={{
                sorting: { sortModel: [{ field: "dropPct", sort: "desc" }] },
              }}
            />
          </Box>
        )}
      </CardContent>
    </GlassCard>
  );
};

export default LeetCodeDropOffSection;
